import { env } from "@/env";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFormUpload, apiJson } from "@/data/api";
import { workspaceKeys } from "@/data/workspaces";

// Present on calendar-triggered sessions (plan 031): the meeting the
// capture was recorded for, in provider-neutral fields.
interface SessionCalendarEvent {
  title: string | null;
  attendees: Array<{ email: string; response_status: string }>;
  starts_at: number | null;
  ends_at: number | null;
}

export interface SpokenMemorySessionSummary {
  id: string;
  processing_window_id: string | null;
  device_name: string | null;
  mode?: number;
  start_trigger?: number;
  calendar_event?: SessionCalendarEvent | null;
  started_at: number;
  ended_at: number | null;
  title: string;
  summary: string;
  status: string;
  block_count: number;
  segment_count: number;
}

export interface PinRow {
  id: string;
  user_id: string;
  source: "capture";
  session_id: string;
  created_at: number;
}

interface SpokenMemorySessionsResponse {
  captures?: SpokenMemorySessionSummary[];
  sessions: SpokenMemorySessionSummary[];
  workspace_role: string | null;
  can_manage_spoken_memory: boolean;
}

interface SpokenMemorySegment {
  id: string;
  speaker_name: string | null;
  local_label: string | null;
  start_ms: number;
  end_ms: number;
  text: string;
  confidence: number | null;
}

interface SpokenMemoryBlock {
  id: string;
  title: string;
  summary: string;
  text: string;
  start_ms: number;
  end_ms: number;
  entities: Record<string, unknown> | null;
}

export interface SpokenMemorySessionDetail {
  session: SpokenMemorySessionSummary;
  speaker_clusters: Array<{
    id: string;
    local_label: string;
    matched_member_name: string | null;
    match_status: string;
    match_confidence: number | null;
    total_speech_ms: number;
  }>;
  segments: SpokenMemorySegment[];
  blocks: SpokenMemoryBlock[];
}

export interface VoiceProfile {
  id: string;
  person_id?: string | null;
  person_name?: string | null;
  representation?: string | null;
  display_name: string;
  member_name: string | null;
  status: string;
  embedding_model: string;
  quality_score: number | null;
  total_speech_ms: number;
  enrollment_source: string;
  consented_at: number | null;
  created_at: number;
  updated_at: number;
  has_sample_audio?: boolean;
  has_hosted_voiceprint?: boolean;
  hosted_voiceprint_id?: string | null;
  sample_status?: "accepted" | "needs_redo" | "processing" | string | null;
  sample_status_reason?: string | null;
  hosted_voiceprint_status?: "pending" | "ready" | "failed" | string | null;
  hosted_voiceprint_status_reason?: string | null;
  attribution_ready?: boolean;
  attribution_status?:
    | "ready"
    | "needs_redo"
    | "missing_hosted_voiceprint"
    | string
    | null;
  attribution_status_reason?: string | null;
  required_representation?: string | null;
}

export interface VoiceProfileDiagnostics {
  config: {
    modal_configured: boolean;
    match_confidence_floor: number;
    matching_method?: string;
    required_representation?: string;
  };
  totals: {
    profile_count: number;
    active_profile_count: number;
    profiles_with_embeddings: number;
    profiles_with_sample_audio: number;
    cluster_count: number;
    matched_cluster_count: number;
    unmatched_cluster_count: number;
    matched_speech_ms: number;
    unresolved_speech_ms: number;
  };
  profiles: Array<
    VoiceProfile & {
      has_embedding: boolean;
      sample_byte_size: number | null;
      matched_cluster_count: number;
      average_match_confidence: number | null;
      last_matched_at: number | null;
    }
  >;
  recent_clusters: Array<{
    id: string;
    session_id: string;
    session_title: string | null;
    session_started_at: number;
    local_label: string;
    match_status: string;
    match_confidence: number | null;
    total_speech_ms: number;
    matched_member_name: string | null;
    segment_count: number;
  }>;
}

export interface SpeakerClusterReviewItem {
  id: string;
  session_id: string;
  local_label: string;
  matched_person_id: string | null;
  matched_member_name: string | null;
  match_status: "unmatched";
  match_confidence: number | null;
  total_speech_ms: number;
  session_title: string | null;
  session_started_at: number;
  session_ended_at: number;
  segment_count: number;
}

export interface PendingAudioChunkGroup {
  workspace_id: string;
  device_id: string;
  device_name: string | null;
  user_id: string;
  chunk_count: number;
  first_captured_at: number;
  last_captured_at: number;
  total_duration_ms: number;
  total_byte_size: number;
}

export interface ProcessingWindowSummary {
  id: string;
  device_id: string;
  device_name: string | null;
  user_id: string;
  workspace_id: string;
  started_at: number;
  ended_at: number;
  status: string;
  finalized_reason: string;
  processing_started_at: number | null;
  processing_finished_at: number | null;
  error: string | null;
  created_at: number;
  updated_at: number;
  chunk_count: number;
  total_duration_ms: number;
  session_id: string | null;
  session_title: string | null;
}

export interface PendingMemorySummary {
  id: string;
  device_id: string;
  device_name: string | null;
  user_id: string;
  workspace_id: string;
  mode: number;
  started_at: number;
  ended_at: number;
  status: "ready" | "processing" | "failed";
  finalized_reason: string;
  processing_started_at: number | null;
  processing_finished_at: number | null;
  error: string | null;
  created_at: number;
  updated_at: number;
}

export interface PendingMemoryBacklogSummary {
  ambient_count: number;
  ambient_ready_count: number;
  ambient_processing_count: number;
  ambient_retrying_count: number;
  ambient_device_count: number;
  ambient_oldest_started_at: number | null;
  ambient_newest_started_at: number | null;
  ambient_latest_activity_at: number | null;
}

export type CaptureActivityState =
  | "ambient_armed"
  | "ambient_capturing"
  | "explicit_recording"
  | "hearing_speech"
  | "settling"
  | "processing"
  | "ready_recently"
  | "continuous_audio"
  | "needs_attention"
  | "idle"
  | "offline";

export interface CaptureActivity {
  device_id: string;
  device_name: string | null;
  device_online: boolean;
  state: CaptureActivityState;
  headline: string;
  detail: string;
  last_activity_at: number | null;
  quiet_for_ms: number | null;
  active_window_id: string | null;
  active_session_id: string | null;
  secondary_statuses?: CaptureActivitySecondaryStatus[];
  diagnostics: CaptureActivityDiagnostic[];
}

export interface CaptureActivitySecondaryStatus {
  state: CaptureActivityState;
  label: string;
  detail: string;
  occurred_at: number | null;
  active_window_id: string | null;
  active_session_id: string | null;
}

export interface CaptureActivityDiagnostic {
  kind: "stale_failed_window";
  window_id: string;
  message: string;
  occurred_at: number | null;
}

export interface SessionAudioChunk {
  chunk_id: string;
  source_chunk_id: string;
  presigned_url: string;
  duration_ms: number;
  byte_size: number;
  captured_at: number;
  session_relative_start_ms: number;
}

interface PipelineProcessingResult {
  processingWindowId: string;
  sessionId: string;
  blockIds: string[];
  segmentIds: string[];
  speakerClusterIds: string[];
  audioChunkCount: number;
  matchedSpeakerCount: number;
  unknownSpeakerCount: number;
  modelMetadata: {
    diarization_model?: string | null;
    embedding_model?: string | null;
    whisper_model?: string | null;
    audio_duration_ms?: number;
    processing_ms?: number;
    match_threshold?: number;
    candidate_profile_count?: number;
    input_chunk_count?: number;
    skipped_reason?: string;
  };
}

interface BrowserEnrollmentResult {
  ok: true;
  sample_id: string;
  sample_status: "accepted" | "needs_redo";
  sample_status_reason: string | null;
  hosted_voiceprint_status: "pending" | null;
  voice_profile: VoiceProfile | null;
}

// Compact server redo reasons → the one concrete next action (plan 027).
export function enrollmentRedoCopy(reason: string | null | undefined): string {
  switch (reason) {
    case "multiple_speakers_detected":
      return "We heard more than one speaker. Redo this alone in a quiet room.";
    case "not_enough_speech":
      return "We did not hear enough speech. Redo and speak continuously.";
    case "too_much_silence":
      return "There was too much silence. Redo and keep speaking until the timer ends.";
    case "audio_decode_failed":
    case "qa_failed":
      return "We could not process the audio. Try again.";
    case "redo_requested":
      return "Your team asked you to redo this voice test. Record a new one.";
    default:
      return reason && reason.trim().length > 0
        ? reason
        : "Voice sample needs a redo.";
  }
}

interface SpokenMemoryApiOptions {
  workspaceSlug?: string | null;
}

function spokenMemoryBasePath(workspaceSlug?: string | null): string {
  if (!workspaceSlug) {
    return `/${["spoken", "memory"].join("-")}`;
  }
  return `/workspaces/${encodeURIComponent(workspaceSlug)}/spoken-memory`;
}

export function createSpokenMemoryApi(options: SpokenMemoryApiOptions = {}) {
  const base = spokenMemoryBasePath(options.workspaceSlug);
  return {
    listSessions: () =>
      apiJson<SpokenMemorySessionsResponse>(`${base}/captures`),
    listSessionPins: () =>
      apiJson<{ pins: PinRow[] }>(`${base}/me/capture-pins`),
    getSession: (id: string) =>
      apiJson<SpokenMemorySessionDetail>(
        `${base}/captures/${encodeURIComponent(id)}`,
      ),
    pinSession: (id: string) =>
      apiJson<{ pin: PinRow }>(
        `${base}/captures/${encodeURIComponent(id)}/my-pin`,
        {
          method: "PUT",
        },
      ),
    unpinSession: (id: string) =>
      apiJson<void>(`${base}/captures/${encodeURIComponent(id)}/my-pin`, {
        method: "DELETE",
      }),
    deleteSession: (id: string) =>
      apiJson<{ ok: true }>(`${base}/captures/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    listVoiceProfiles: () =>
      apiJson<{ voice_profiles: VoiceProfile[] }>(`${base}/voice-profiles`),
    getMyVoiceProfile: () =>
      apiJson<{ voice_profile: VoiceProfile | null }>(
        `${base}/voice-profiles/me`,
      ),
    getVoiceProfileDiagnostics: () =>
      apiJson<VoiceProfileDiagnostics>(`${base}/voice-profiles/diagnostics`),
    listSpeakerClusterReviews: () =>
      apiJson<{ speaker_clusters: SpeakerClusterReviewItem[] }>(
        `${base}/speaker-clusters/needs-review`,
      ),
    claimSpeakerCluster: (id: string) =>
      apiJson<{ ok: true; speaker_cluster: Record<string, unknown> }>(
        `${base}/speaker-clusters/${encodeURIComponent(id)}/claim`,
        { method: "POST", json: {} },
      ),
    deleteVoiceProfile: (id: string) =>
      apiJson<{
        ok: true;
        voice_profile: Pick<VoiceProfile, "id" | "display_name" | "status">;
      }>(`${base}/voice-profiles/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    voiceProfileSampleAudioUrl: (id: string) =>
      `${env.API_BASE_URL}${base}/voice-profiles/${encodeURIComponent(id)}/sample-audio`,
    listProcessingWindows: () =>
      apiJson<{ processing_windows: ProcessingWindowSummary[] }>(
        `${base}/processing-windows`,
      ),
    listPendingMemories: () =>
      apiJson<{
        pending_memories: PendingMemorySummary[];
        pending_memory_backlog?: PendingMemoryBacklogSummary;
      }>(`${base}/pending-memories`),
    listCaptureActivity: () =>
      apiJson<{ capture_activity: CaptureActivity[] }>(
        `${base}/capture-activity`,
      ),
    listPendingAudio: () =>
      apiJson<{ pending_audio: PendingAudioChunkGroup[] }>(
        `${base}/pending-audio`,
      ),
    manualFinalizeProcessingWindow: (input: {
      deviceId: string;
      startedAt?: number;
      endedAt?: number;
      processNow?: boolean;
    }) =>
      apiJson<{
        ok: true;
        processing_window: {
          id: string;
          device_id: string;
          user_id: string;
          workspace_id: string;
          started_at: number;
          ended_at: number;
          status: string;
          finalized_reason: string;
          audio_chunk_count: number;
        };
        processed: PipelineProcessingResult | null;
      }>(`${base}/processing-windows/manual-finalize`, {
        method: "POST",
        json: {
          device_id: input.deviceId,
          started_at: input.startedAt,
          ended_at: input.endedAt,
          process_now: input.processNow ?? false,
        },
      }),
    processProcessingWindow: (id: string) =>
      apiJson<{ ok: true; processed: PipelineProcessingResult }>(
        `${base}/processing-windows/${encodeURIComponent(id)}/process`,
        { method: "POST", json: {} },
      ),
    listSessionAudioChunks: (id: string) =>
      apiJson<{ chunks: SessionAudioChunk[] }>(
        `${base}/captures/${encodeURIComponent(id)}/audio-chunks`,
      ),
    seedFixture: () =>
      apiJson<{ ok: true; sessionId: string; blockIds: string[] }>(
        `${base}/dev/seed`,
        { method: "POST", json: {} },
      ),
    uploadBrowserEnrollment: (
      form: FormData,
      options: { onUploadComplete?: () => void } = {},
    ) =>
      apiFormUpload<BrowserEnrollmentResult>(
        `${base}/voice-profiles/browser-enrollment`,
        form,
        options,
      ),
    retrySpeakerSetup: () =>
      apiJson<{ ok: true; sample_id: string }>(
        `${base}/voice-profiles/me/retry-speaker-setup`,
        { method: "POST", json: {} },
      ),
  };
}

export type SpokenMemoryApi = ReturnType<typeof createSpokenMemoryApi>;

export const spokenMemoryApi = createSpokenMemoryApi();

export const spokenMemoryKeys = {
  all: ["spoken-memory"] as const,
  sessions: (workspaceSlug: string | null | undefined) =>
    [...spokenMemoryKeys.all, "sessions", workspaceSlug ?? null] as const,
  pins: (workspaceSlug: string | null | undefined) =>
    [...spokenMemoryKeys.all, "pins", workspaceSlug ?? null] as const,
  session: (
    workspaceSlug: string | null | undefined,
    sessionId: string | null | undefined,
  ) =>
    [
      ...spokenMemoryKeys.all,
      "session",
      workspaceSlug ?? null,
      sessionId ?? null,
    ] as const,
  pendingMemories: (workspaceSlug: string | null | undefined) =>
    [
      ...spokenMemoryKeys.all,
      "pending-memories",
      workspaceSlug ?? null,
    ] as const,
  captureActivity: (workspaceSlug: string | null | undefined) =>
    [
      ...spokenMemoryKeys.all,
      "capture-activity",
      workspaceSlug ?? null,
    ] as const,
  myVoiceProfile: (workspaceSlug: string | null | undefined) =>
    [
      ...spokenMemoryKeys.all,
      "my-voice-profile",
      workspaceSlug ?? null,
    ] as const,
};

export function useSpokenMemorySessionsQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: spokenMemoryKeys.sessions(workspaceSlug),
    queryFn: () => createSpokenMemoryApi({ workspaceSlug }).listSessions(),
    enabled: Boolean(workspaceSlug),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useSessionPinsQuery(workspaceSlug: string | null | undefined) {
  return useQuery({
    queryKey: spokenMemoryKeys.pins(workspaceSlug),
    queryFn: () => createSpokenMemoryApi({ workspaceSlug }).listSessionPins(),
    enabled: Boolean(workspaceSlug),
    staleTime: 10_000,
  });
}

export function useSessionDetailQuery(
  workspaceSlug: string | null | undefined,
  sessionId: string | null | undefined,
) {
  return useQuery({
    queryKey: spokenMemoryKeys.session(workspaceSlug, sessionId),
    queryFn: () =>
      createSpokenMemoryApi({ workspaceSlug }).getSession(sessionId ?? ""),
    enabled: Boolean(workspaceSlug && sessionId),
    refetchInterval: (query) => sessionDetailRefetchInterval(query.state.data),
    staleTime: 5_000,
  });
}

export function usePendingMemoriesQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: spokenMemoryKeys.pendingMemories(workspaceSlug),
    queryFn: () =>
      createSpokenMemoryApi({ workspaceSlug }).listPendingMemories(),
    enabled: Boolean(workspaceSlug),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useCaptureActivityQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: spokenMemoryKeys.captureActivity(workspaceSlug),
    queryFn: () =>
      createSpokenMemoryApi({ workspaceSlug }).listCaptureActivity(),
    enabled: Boolean(workspaceSlug),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useMyVoiceProfileQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: spokenMemoryKeys.myVoiceProfile(workspaceSlug),
    queryFn: () => createSpokenMemoryApi({ workspaceSlug }).getMyVoiceProfile(),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function sessionDetailRefetchInterval(
  detail: SpokenMemorySessionDetail | undefined,
): number | false {
  return detail?.session.status === "processing" ? 8_000 : false;
}

export function useDeleteSessionMutation(
  workspaceSlug: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (sessionId) =>
      createSpokenMemoryApi({ workspaceSlug }).deleteSession(sessionId),
    onSuccess: (_data, sessionId) => {
      queryClient.removeQueries({
        queryKey: spokenMemoryKeys.session(workspaceSlug, sessionId),
      });
      void queryClient.invalidateQueries({
        queryKey: spokenMemoryKeys.sessions(workspaceSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: spokenMemoryKeys.pins(workspaceSlug),
      });
    },
  });
}

export function usePinSessionMutation(
  workspaceSlug: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { sessionId: string; pinned: boolean }>({
    mutationFn: async (input) => {
      if (input.pinned) {
        await createSpokenMemoryApi({ workspaceSlug }).pinSession(
          input.sessionId,
        );
      } else {
        await createSpokenMemoryApi({ workspaceSlug }).unpinSession(
          input.sessionId,
        );
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: spokenMemoryKeys.pins(workspaceSlug),
      });
    },
  });
}

export function useDeleteVoiceProfileMutation(
  workspaceSlug: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) =>
      createSpokenMemoryApi({ workspaceSlug }).deleteVoiceProfile(profileId),
    onSuccess: () => {
      invalidateVoiceProfileQueries(queryClient, workspaceSlug);
    },
  });
}

export function useBrowserEnrollmentMutation(
  workspaceSlug: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      audio: Blob;
      mimeType: string;
      onUploadComplete?: () => void;
    }) => {
      const form = new FormData();
      form.append(
        "audio",
        input.audio,
        input.mimeType.includes("mp4") ? "enrollment.mp4" : "enrollment.webm",
      );
      form.append("consent", "true");
      return createSpokenMemoryApi({ workspaceSlug }).uploadBrowserEnrollment(
        form,
        { onUploadComplete: input.onUploadComplete },
      );
    },
    onSuccess: () => {
      invalidateVoiceProfileQueries(queryClient, workspaceSlug);
    },
  });
}

export function useRetrySpeakerSetupMutation(
  workspaceSlug: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      createSpokenMemoryApi({ workspaceSlug }).retrySpeakerSetup(),
    onSuccess: () => {
      invalidateVoiceProfileQueries(queryClient, workspaceSlug);
    },
  });
}

function invalidateVoiceProfileQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceSlug: string | null | undefined,
) {
  void queryClient.invalidateQueries({
    queryKey: spokenMemoryKeys.myVoiceProfile(workspaceSlug),
  });
  // A new accepted sample flips the onboarding gate flag on /workspaces/me
  // (plan 029).
  void queryClient.invalidateQueries({ queryKey: workspaceKeys.me() });
}
